import Skeleton from 'react-loading-skeleton';
import styles from './Metric.module.scss';
import { ReactNode, useCallback } from 'react';
import BadgeDot, { BadgeStatus } from '@cas/components/data-display/badge-dot/BadgeDot';
import { useLocaleFormatter } from '@customer/shared/hooks/useLocaleFormatter';

export enum MetricSize {
  S,
  M,
  L,
  XL,
}

export interface MetricProps {
  label?: ReactNode;
  dataPrimary?: number | null;
  dataSecondary?: number | null;
  isFraction?: boolean;
  status?: BadgeStatus;
  size?: MetricSize;
}

export const Metric = ({
  label,
  dataPrimary,
  dataSecondary,
  status,
  isFraction = false,
  size = MetricSize.L,
}: MetricProps) => {
  const { getNumberText } = useLocaleFormatter();

  const getNumberDisplay = useCallback(
    (value: number | null): string => {
      // null is used to indicate the value is ignored or not applicable.
      return typeof value === 'number' ? getNumberText(value) : '-';
    },
    [getNumberText],
  );

  const isLoaded = typeof dataPrimary !== 'undefined';

  return (
    <div className={`${styles.metric} ${size === MetricSize.XL && styles['metric--xl']}`}>
      {label && (
        <div
          className={`${styles.metric__label} ${size === MetricSize.XL && styles['metric__label--xl']}`}
        >
          {label}
        </div>
      )}

      <div className={styles.data}>
        {status && <BadgeDot status={status} size={size >= MetricSize.L ? 12 : 8} />}

        {isLoaded ? (
          <>
            <div
              className={`${styles.data__primary} ${size === MetricSize.M && styles['data__primary--md']} ${size >= MetricSize.L && styles['data__primary--lg']}`}
            >
              {getNumberDisplay(dataPrimary)}
            </div>
            {typeof dataSecondary !== 'undefined' && (
              <div
                className={`${styles.data__secondary} ${isFraction && styles['data__secondary--fraction']} ${size === MetricSize.S && styles['data__secondary--sm']}`}
              >
                {isFraction && <span className={styles.data__separator}>/</span>}
                {getNumberDisplay(dataSecondary)}
              </div>
            )}
          </>
        ) : (
          <Skeleton
            width={80}
            height={size >= MetricSize.M ? (size >= MetricSize.L ? 38 : 30) : 26}
          />
        )}
      </div>
    </div>
  );
};

export default Metric;
